/**
 * Import accounts… on the Credit control page (docs/features/credit-control/
 * 06_ui_strategy.md §2, "Import dialog"): load credit accounts from a CSV in
 * three steps.
 *  1. File: choose a CSV, give it a label, or download the template: every
 *     customer that traded on account in the last 12 months, ready for the
 *     limits to be typed in.
 *  2. Check results: the dry run says how many rows are new, changed,
 *     unchanged and rejected, and names the first rejections; nothing is
 *     written yet. Back returns to step 1.
 *  3. Imported: the count imported and the count rejected (not imported).
 *
 * Generic on purpose: finance fills the template from whatever records they
 * keep, and the label is free text. No source system is named.
 */
import { useRef, useState } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  type CreditImportCheck,
  type CreditImportInput,
  type CreditImportResult,
} from "@shared-core/data/credit-control-types";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";

/** How many rejections step 2 lists before "Show all rejections". */
const REJECTIONS_PREVIEWED = 2;

type Step =
  | { kind: "file" }
  | { kind: "checked"; input: CreditImportInput; check: CreditImportCheck }
  | { kind: "imported"; result: CreditImportResult };

const count = (n: number): string => n.toLocaleString("en-GB");

export function ImportCreditAccountsDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once an import has run, so the list behind can reload. */
  onImported: () => void;
}) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const fileInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: "file" });
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setStep({ kind: "file" });
      setFile(null);
      setLabel("");
      setShowAll(false);
      setError(null);
    }
  };

  const downloadTemplate = async () => {
    setBusy(true);
    setError(null);
    try {
      const template = await adapter.getCreditImportTemplate();
      capabilities.downloadTextFile(
        template.filename,
        template.csvText,
        "text/csv",
      );
    } catch (err) {
      setError(mutationErrorMessage(err, "Could not download the template."));
    } finally {
      setBusy(false);
    }
  };

  const checkFile = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const input: CreditImportInput = {
        fileName: file.name,
        csvText: await file.text(),
        label: label.trim(),
      };
      setStep({
        kind: "checked",
        input,
        check: await adapter.checkCreditImport(input),
      });
      setShowAll(false);
    } catch (err) {
      setError(mutationErrorMessage(err, "Could not check the file."));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (input: CreditImportInput) => {
    setBusy(true);
    setError(null);
    try {
      const result = await adapter.runCreditImport(input);
      setStep({ kind: "imported", result });
      onImported();
    } catch (err) {
      setError(mutationErrorMessage(err, "Could not import the accounts."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Import payment settings</DialogTitle>
          <DialogDescription>
            Load each payer's payment methods and credit terms from a CSV.
            Nothing is changed until you have seen the check.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step.kind === "file" && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">1 File</span>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                aria-label="CSV file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInput.current?.click()}
              >
                Choose CSV…
              </Button>
              <span className="text-muted-foreground">
                {file ? file.name : "No file chosen"}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={busy}
                onClick={() => void downloadTemplate()}
              >
                Download template
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credit-import-label">Label</Label>
              <Input
                id="credit-import-label"
                value={label}
                placeholder="Payment settings, October 2026"
                onChange={(e) => setLabel(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Recorded against every account the import creates or changes.
              </p>
              <p className="text-xs text-muted-foreground">
                A po_format column sets each row&apos;s PO format, and a blank
                cell clears it. A file without that column leaves every PO
                format as it is.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!file || label.trim() === "" || busy}
                onClick={() => void checkFile()}
              >
                Check file
              </Button>
            </DialogFooter>
          </div>
        )}

        {step.kind === "checked" && (
          <CheckedStep
            check={step.check}
            showAll={showAll}
            onShowAll={() => setShowAll(true)}
            busy={busy}
            onBack={() => {
              setError(null);
              setStep({ kind: "file" });
            }}
            onImport={() => void runImport(step.input)}
          />
        )}

        {step.kind === "imported" && (
          <div className="space-y-4 text-sm">
            <p>
              <span className="font-medium">3 Imported</span>{" "}
              {count(step.result.imported)}{" "}
              {step.result.imported === 1 ? "account" : "accounts"}.
              {step.result.rejected > 0 &&
                ` ${count(step.result.rejected)} ${step.result.rejected === 1 ? "row" : "rows"} rejected (not imported).`}
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CheckedStep({
  check,
  showAll,
  onShowAll,
  busy,
  onBack,
  onImport,
}: {
  check: CreditImportCheck;
  showAll: boolean;
  onShowAll: () => void;
  busy: boolean;
  onBack: () => void;
  onImport: () => void;
}) {
  const importable = check.created + check.updated;
  const shown = showAll
    ? check.rejections
    : check.rejections.slice(0, REJECTIONS_PREVIEWED);
  const hidden = check.rejections.length - shown.length;
  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-1">
        <p>
          <span className="font-medium">2 Checked:</span>{" "}
          {count(check.totalRows)} rows
        </p>
        <p>
          {count(check.created)} new · {count(check.updated)} changed ·{" "}
          {count(check.unchanged)} unchanged · {count(check.rejected)} rejected
        </p>
      </div>
      {shown.length > 0 && (
        <ul className="space-y-1 rounded-md border border-border p-3">
          {shown.map((rejection) => (
            <li key={`${rejection.row}-${rejection.field}`}>
              <span className="font-medium">Row {rejection.row}</span>{" "}
              <span className="text-muted-foreground">{rejection.field}</span>{" "}
              {rejection.message}
            </li>
          ))}
          {hidden > 0 && (
            <li className="flex items-center justify-between text-muted-foreground">
              <span>… {count(hidden)} more</span>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={onShowAll}
              >
                Show all rejections
              </Button>
            </li>
          )}
        </ul>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={importable === 0 || busy}
          onClick={onImport}
        >
          Import {count(importable)} {importable === 1 ? "account" : "accounts"}
        </Button>
      </DialogFooter>
    </div>
  );
}
